import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DURATION_OPTIONS = [
  { days: 1, label: 'يوم واحد' },
  { days: 3, label: '3 أيام' },
  { days: 7, label: 'أسبوع' },
  { days: 30, label: 'شهر' },
  { days: null, label: 'غير محدد' },
];

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ar-SA', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return null;
  }
}

function getHeaders() {
  const userId = localStorage.getItem('userId');
  return userId ? { 'X-User-Id': userId } : {};
}

export default function PrivacyPause() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedDays, setSelectedDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/account/pause-status`,
        { withCredentials: true, headers: getHeaders() }
      );
      setStatus(res.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handlePause = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/account/pause`,
        { duration_days: selectedDays },
        { withCredentials: true, headers: getHeaders() }
      );
      await fetchStatus();
      setShowOptions(false);
      setShowConfirm(false);
    } catch (e) {
      alert('فشل إخفاء الحساب. حاول مجدداً.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpause = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/account/unpause`,
        {},
        { withCredentials: true, headers: getHeaders() }
      );
      await fetchStatus();
    } catch (e) {
      alert('فشل استئناف الحساب. حاول مجدداً.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const isPaused = status?.is_paused;
  const untilLabel = formatDate(status?.paused_until);

  return (
    <div className="card" data-testid="privacy-pause-section" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
        <span style={{ fontSize: '28px' }}>🌙</span>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '17px', color: 'var(--text-dark)' }}>
            إخفاء الحساب مؤقتاً
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-light)', lineHeight: 1.6 }}>
            استرح بدون حذف حسابك — يختفي ملفك من نتائج البحث ويعود تلقائياً
          </p>
        </div>
      </div>

      {isPaused ? (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.08))',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{
              display: 'inline-block', width: '10px', height: '10px',
              borderRadius: '50%', background: '#F59E0B',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}></span>
            <strong style={{ color: '#92400E', fontSize: '14px' }}>
              حسابك مخفي حالياً عن نتائج البحث
            </strong>
          </div>
          {untilLabel ? (
            <p style={{ margin: 0, fontSize: '13px', color: '#92400E' }}>
              يعود تلقائياً في: <strong>{untilLabel}</strong>
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: '#92400E' }}>
              مدة الإخفاء: <strong>غير محددة</strong> — استأنف عندما تشاء
            </p>
          )}
          <button
            data-testid="resume-account-btn"
            onClick={handleUnpause}
            disabled={saving}
            style={{
              marginTop: '12px',
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #10B981, #059669)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
            }}
          >
            {saving ? '⏳ جاري الاستئناف...' : '✨ استئناف الحساب الآن'}
          </button>
        </div>
      ) : (
        <>
          {!showOptions ? (
            <button
              data-testid="open-pause-options-btn"
              onClick={() => setShowOptions(true)}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: 'var(--primary)',
                border: '2px solid var(--primary)',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              🌙 إخفاء حسابي مؤقتاً
            </button>
          ) : (
            <div>
              <p style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '10px' }}>
                اختر مدة الإخفاء:
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                gap: '8px',
                marginBottom: '14px'
              }}>
                {DURATION_OPTIONS.map(opt => (
                  <button
                    key={String(opt.days)}
                    data-testid={`pause-duration-${opt.days ?? 'unlimited'}`}
                    onClick={() => setSelectedDays(opt.days)}
                    style={{
                      padding: '10px',
                      background: selectedDays === opt.days
                        ? 'linear-gradient(135deg, var(--primary), var(--purple))'
                        : 'transparent',
                      color: selectedDays === opt.days ? 'white' : 'var(--text-dark)',
                      border: `2px solid ${selectedDays === opt.days ? 'transparent' : 'rgba(0,0,0,0.1)'}`,
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => { setShowOptions(false); setShowConfirm(false); }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'transparent',
                    color: 'var(--text-light)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  إلغاء
                </button>
                <button
                  data-testid="confirm-pause-btn"
                  onClick={() => setShowConfirm(true)}
                  style={{
                    flex: 2,
                    padding: '12px',
                    background: 'linear-gradient(135deg, var(--primary), var(--purple))',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  تأكيد الإخفاء
                </button>
              </div>
              {showConfirm && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'rgba(255, 152, 0, 0.08)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    borderRadius: '10px'
                  }}
                >
                  <p style={{ fontSize: '13px', color: 'var(--text-dark)', margin: '0 0 10px' }}>
                    سيتم إخفاء ملفك عن نتائج البحث
                    {selectedDays
                      ? <> لمدة <strong>{DURATION_OPTIONS.find(o => o.days === selectedDays)?.label}</strong></>
                      : <> <strong>إلى أجل غير محدد</strong></>
                    }.
                    <br />
                    محادثاتك ومفضلتك تبقى كما هي. هل تريد المتابعة؟
                  </p>
                  <button
                    data-testid="final-pause-btn"
                    onClick={handlePause}
                    disabled={saving}
                    style={{
                      width: '100%',
                      padding: '11px',
                      background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {saving ? '⏳ جاري الإخفاء...' : '🌙 نعم، أخفِ حسابي'}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}
